import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import { getData } from "../../helpers/url-request";
import { getAuthenticatedUser } from "../../helpers/auth-session";
import { useContextualHelp } from "../../components/Help/ContextualHelp";
import "./FirmAdminDashboard.scss";

const areas = [
  [
    "/settings/security",
    "users",
    "blue",
    "Users & Access",
    "Invite users, assign roles and manage access to your firm.",
  ],
  [
    "/system/checklist",
    "list",
    "green",
    "Checklists",
    "Create, import and maintain checklist templates.",
  ],
  [
    "/system/standards",
    "book-open",
    "violet",
    "Standards & References",
    "Manage professional standards and reference documents.",
  ],
  [
    "/firm/settings",
    "settings",
    "orange",
    "Firm Settings",
    "Manage firm information, preferences and configuration.",
  ],
];
const Icon = ({ name, tone }) => (
  <span className={`firm-admin-home__icon ${tone}`}>
    <FeatherIcon icon={name} />
  </span>
);
const number = (value) =>
  value == null ? "—" : Number(value).toLocaleString();

export default function FirmAdminDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const { openHelp } = useContextualHelp();
  useEffect(() => {
    let active = true;
    setError("");
    getData("/dashboard/data/administration")
      .then((response) => {
        if (response?.status === false || !response?.data)
          throw new Error("Unable to load administration summary.");
        if (active) setData(response.data);
      })
      .catch(() => {
        if (active)
          setError("Unable to load administration summary. Please try again.");
      });
    return () => {
      active = false;
    };
  }, [attempt]);
  const metrics = [
    [
      "/settings/security",
      "users",
      "blue",
      "Total Users",
      data?.totalUsers,
      data
        ? `${number(data.activeUsers)} active  ·  ${number(
            data.pendingUsers
          )} pending`
        : "Loading users…",
    ],
    [
      "/system/checklist",
      "file-text",
      "green",
      "Checklist Questions",
      data?.checklistQuestions,
      data
        ? `Across ${number(data.checklists)} checklists`
        : "Loading checklists…",
    ],
    [
      "/system/standards",
      "file-text",
      "violet",
      "Standards & References",
      data?.standards,
      "Standards available",
    ],
  ];
  return (
    <main className="firm-admin-home page-content piers-page">
      <header className="firm-admin-home__header piers-page-header">
        <div>
          <p className="firm-admin-home__eyebrow">Administration</p>
          <h1 className="piers-page-title">Welcome to Administration</h1>
          <p>Manage your firm's users, checklists, standards and settings.</p>
        </div>
        <span className="firm-admin-home__firm">
          <FeatherIcon icon="briefcase" />
          {data?.firmName || getAuthenticatedUser()?.firmName || "Your firm"}
        </span>
      </header>
      {error && (
        <div className="firm-admin-home__error" role="alert">
          {error}
          <button
            type="button"
            onClick={() => setAttempt((value) => value + 1)}
          >
            Retry
          </button>
        </div>
      )}
      <section
        className="firm-admin-home__metrics"
        aria-label="Firm summary"
        aria-busy={!data && !error}
      >
        {metrics.map(([to, icon, tone, title, value, detail]) => (
          <Link to={to} className="firm-admin-home__metric" key={title}>
            <Icon name={icon} tone={tone} />
            <div>
              <h2>{title}</h2>
              <strong>{number(value)}</strong>
              <p>{error && !data ? "Summary unavailable" : detail}</p>
            </div>
            <FeatherIcon
              icon="chevron-right"
              className="firm-admin-home__arrow"
            />
          </Link>
        ))}
      </section>
      <section aria-labelledby="firm-management-title">
        <div className="firm-admin-home__section-heading">
          <h2 id="firm-management-title">Manage your firm</h2>
          <p>
            Select an area below to manage your firm's configuration and data.
          </p>
        </div>
        <div className="firm-admin-home__areas">
          {areas.map(([to, icon, tone, title, description]) => (
            <Link to={to} className="firm-admin-home__area" key={title}>
              <Icon name={icon} tone={tone} />
              <div>
                <h3>{title}</h3>
                <p>{description}</p>
              </div>
              <FeatherIcon
                icon="chevron-right"
                className="firm-admin-home__arrow"
              />
            </Link>
          ))}
        </div>
      </section>
      <aside className="firm-admin-home__help">
        <FeatherIcon icon="info" />
        <div>
          <h2>Need help?</h2>
          <p>
            Visit the PIERS Guide for step-by-step instructions or contact your
            system administrator.
          </p>
        </div>
        <button type="button" onClick={openHelp}>
          <FeatherIcon icon="book-open" />
          Open PIERS Guide
          <FeatherIcon icon="external-link" />
        </button>
      </aside>
    </main>
  );
}
